import type { TFunction } from 'i18next';

import { formatDays, formatTimeDisplay } from './coverageHoursTeamUtils';
import {
  CoverageHoursDayOfWeek,
  CoverageHoursRouting,
} from './useCoverageHours';

const DAY_CODE_BY_PROTO_NAME: Record<CoverageHoursDayOfWeek, string> = {
  DAY_OF_WEEK_SUNDAY: 'SUN',
  DAY_OF_WEEK_MONDAY: 'MON',
  DAY_OF_WEEK_TUESDAY: 'TUE',
  DAY_OF_WEEK_WEDNESDAY: 'WED',
  DAY_OF_WEEK_THURSDAY: 'THU',
  DAY_OF_WEEK_FRIDAY: 'FRI',
  DAY_OF_WEEK_SATURDAY: 'SAT',
};

const DAY_CODE_BY_NAME: Record<string, string | undefined> =
  DAY_CODE_BY_PROTO_NAME;

type CoverageHoursSchedule = {
  daysOfWeek: string[];
  startMinute: number;
  endMinute: number;
};

export function toDayCodes(daysOfWeek: string[]): string[] {
  return daysOfWeek.map(day => {
    const code = DAY_CODE_BY_NAME[day];

    // A day silently dropped from a schedule is as wrong as a day shifted by one.
    if (!code) {
      throw new Error(`Unrecognised coverage hours day of week: ${day}`);
    }

    return code;
  });
}

const PROTO_NAME_BY_DAY_CODE: Record<string, CoverageHoursDayOfWeek> =
  Object.fromEntries(
    Object.entries(DAY_CODE_BY_PROTO_NAME).map(([proto, code]) => [
      code,
      proto as CoverageHoursDayOfWeek,
    ])
  );

export function toProtoDayNames(dayCodes: string[]): CoverageHoursDayOfWeek[] {
  return dayCodes.map(code => {
    const proto = PROTO_NAME_BY_DAY_CODE[code];

    // A day silently dropped from a schedule is as wrong as a day shifted by one.
    if (!proto) {
      throw new Error(`Unrecognised coverage hours day code: ${code}`);
    }

    return proto;
  });
}

// Stored minutes are wall clock in the property's zone. formatTimeDisplay parses the
// ISO string and converts to the viewer's zone, so building the Date in local time is
// what makes that conversion a no-op; a UTC-built date shifts by the viewer's offset.
export function minutesToLocalIso(minutesPastMidnight: number): string {
  const hours = Math.floor(minutesPastMidnight / 60);
  const minutes = minutesPastMidnight % 60;

  return new Date(2000, 0, 1, hours, minutes).toISOString();
}

export function formatCoverageHours(schedule: CoverageHoursSchedule): string {
  const days = formatDays(toDayCodes(schedule.daysOfWeek));
  const start = formatTimeDisplay(minutesToLocalIso(schedule.startMinute));
  const end = formatTimeDisplay(minutesToLocalIso(schedule.endMinute));

  return days ? `${days}, ${start}-${end}` : `${start}-${end}`;
}

export type CoverageHoursSummary = { label: string; isCount: boolean };

export function coverageHoursTeamSummary(
  routing: CoverageHoursRouting,
  t: TFunction
): CoverageHoursSummary {
  const teamNames = [
    ...new Set(
      routing.blocks
        .map(block => block.teamName)
        .filter((name): name is string => Boolean(name))
    ),
  ];

  if (teamNames.length > 1) {
    return {
      label: t(
        'web.admin.serviceRequest.settings.coverageHours.table.teamsAssigned',
        { count: teamNames.length }
      ),
      isCount: true,
    };
  }

  return {
    label:
      teamNames[0] ??
      t(
        'web.admin.serviceRequest.settings.coverageHours.table.teamUnavailable'
      ),
    isCount: false,
  };
}

export function coverageHoursCoverageSummary(
  routing: CoverageHoursRouting,
  t: TFunction
): CoverageHoursSummary {
  if (routing.blocks.length === 0) {
    return {
      label: t(
        'web.admin.serviceRequest.settings.coverageHours.table.noShifts'
      ),
      isCount: false,
    };
  }

  if (routing.blocks.length > 1) {
    return {
      label: t(
        'web.admin.serviceRequest.settings.coverageHours.table.shiftCount',
        { count: routing.blocks.length }
      ),
      isCount: true,
    };
  }

  return { label: formatCoverageHours(routing.blocks[0]), isCount: false };
}

export type CoverageHoursRowViewModel = {
  categoryIssue: string;
  team: CoverageHoursSummary;
  coverage: CoverageHoursSummary;
};

export function createCoverageHoursRowViewModel(
  routing: CoverageHoursRouting,
  t: TFunction
): CoverageHoursRowViewModel {
  const issue =
    routing.issue ??
    t('web.admin.serviceRequest.settings.coverageHours.create.none');

  return {
    categoryIssue: `${routing.category} – ${issue}`,
    team: coverageHoursTeamSummary(routing, t),
    coverage: coverageHoursCoverageSummary(routing, t),
  };
}
